import { BookOpen, Inbox, Zap } from "lucide-react";
import { useState } from "react";
import { AgentDetail } from "../components/agents/agent-detail";
import { RouteRow } from "../components/agents/route-row";
import { SectionGroup } from "../components/agents/section-group";
import { PageHeader, PageSkeleton, Stat } from "../components/page";
import { ColorDot } from "../components/status-badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Sheet, SheetContent } from "../components/ui/sheet";
import { shortRepo } from "../lib/format";
import { useFactory } from "../store";

/** Read-only roster of the stations configured in the repository (packages/agents). */
export function AgentsPage() {
  const { layout, state, agentById } = useFactory();
  const [selected, setSelected] = useState<string | null>(null);
  if (!layout) return <PageSkeleton />;
  const agent = selected ? agentById.get(selected) : undefined;

  return (
    <>
      <PageHeader title="Agents" description="The stations on the line and how work flows between them." />
      <Alert>
        <BookOpen />
        <AlertDescription>
          <span>
            Each station is a markdown file in{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">packages/agents/src/agents</code>
            , and the line that runs them is{" "}
            <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
              packages/agents/src/factory.ts
            </code>
            . Change either, open a pull request, and Skynet rebuilds the line on the next deploy.
          </span>
        </AlertDescription>
      </Alert>

      <div className="grid gap-6 @5xl/main:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Route</CardTitle>
            <CardDescription>
              Every work order passes intake, and intake picks the lane it takes next
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <RouteRow
              label={
                <>
                  <Inbox className="size-4 text-muted-foreground" /> Intake
                </>
              }
              description="Picks the lane"
              stations={layout.intake}
              onSelect={setSelected}
            />
            <div className="ml-2 space-y-5 border-l pl-4">
              {Object.entries(layout.lanes).map(([id, lane]) => (
                <RouteRow
                  key={id}
                  label={
                    <>
                      {id === "hotfix" ? (
                        <Zap className="size-4 text-amber-500" />
                      ) : (
                        <ColorDot color={lane.color} className="size-2.5 ring-0" />
                      )}
                      {lane.name}
                    </>
                  }
                  description={lane.description}
                  stations={lane.stations}
                  onSelect={setSelected}
                />
              ))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Issue tracker</CardTitle>
            <CardDescription>
              Native Skynet issues drive the line · {shortRepo(layout.repository.url)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              <Stat label="tracker" value={layout.tracker.provider} />
              <Stat label="opt-in label" value={layout.tracker.optInLabel || "every issue is eligible"} />
              <Stat
                label="merge"
                value={`${layout.repository.mergeMethod}${layout.repository.requireHumanApproval ? " · approval required" : ""}`}
              />
            </dl>
          </CardContent>
        </Card>
      </div>

      {layout.sections
        .filter((s) => !s.detached)
        .map((section) => (
          <SectionGroup key={section.id} section={section} state={state} onOpen={setSelected} />
        ))}

      {layout.sections.some((s) => s.detached) && (
        <>
          <Separator />
          {layout.sections
            .filter((s) => s.detached)
            .map((section) => (
              <SectionGroup key={section.id} section={section} state={state} onOpen={setSelected} />
            ))}
        </>
      )}

      <Sheet open={Boolean(agent)} onOpenChange={(open) => !open && setSelected(null)}>
        <SheetContent className="gap-0 data-[side=right]:w-full data-[side=right]:sm:max-w-2xl data-[side=right]:lg:max-w-4xl">
          {agent && <AgentDetail agent={agent} />}
        </SheetContent>
      </Sheet>
    </>
  );
}
